import React from 'react';
import { LayoutGrid } from 'lucide-react';

/**
 * PageLayout — the standard screen structure for CCAN frontend screens.
 *
 * Every screen composes a fixed three-region layout:
 *   <PageLayout>
 *     <PageHeader title="..." subtitle="..." icon={Icon} actions={<>...</>} />
 *     <PageBody>...scrollable content...</PageBody>
 *     <PageFooter>...optional sticky footer...</PageFooter>
 *   </PageLayout>
 *
 * - Header and footer are fixed (do not scroll); only the body scrolls.
 * - PageLayout fills its parent height (works inside the app shell's content region,
 *   which is `flex-1 flex flex-col`, and inside a fixed-height modal container).
 * - Footer is optional — omit it for screens that don't need one; the structure is the
 *   same either way, which is the whole point (consistency across screens).
 *
 * This is presentation-only: no data, no app coupling, so any screen can adopt it.
 */

export const PageLayout = ({ children, className = '' }) => (
  <div className={`flex flex-col h-full min-h-0 bg-gray-50 ${className}`}>
    {children}
  </div>
);

export const PageHeader = ({ title, icon: Icon, actions, className = '' }) => (
  <header className={`shrink-0 bg-white border-b border-gray-200 px-6 h-16 flex items-center gap-4 ${className}`}>
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <div className="bg-blue-50 text-blue-600 p-2 rounded-lg shrink-0">
        {Icon ? <Icon className="h-5 w-5" /> : <LayoutGrid className="h-5 w-5" />}
      </div>
      <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </header>
);

export const PageBody = ({ children, className = '', maxWidth = '', padded = true }) => (
  <main className={`flex-1 min-h-0 overflow-y-auto ${padded ? 'px-6 py-6' : ''} ${className}`}>
    {maxWidth ? <div className={`mx-auto w-full ${maxWidth}`}>{children}</div> : children}
  </main>
);

export const PageFooter = ({ children, className = '' }) => (
  <footer className={`shrink-0 bg-white border-t border-gray-200 px-6 py-3 ${className}`}>
    {children}
  </footer>
);

export default PageLayout;
